import React, { useEffect, useRef } from 'react';
import { aboutContent } from '../data/mock';
import { Dna, Cpu, TrendingUp } from 'lucide-react';

const About = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal').forEach((el, index) => {
              setTimeout(() => {
                el.classList.add('active');
              }, index * 150);
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-28 bg-white relative overflow-hidden">
      {/* Premium background decoration */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-indigo-100/40 to-transparent rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-cyan-100/40 to-transparent rounded-full blur-3xl"></div>
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-gradient-to-br from-amber-100/20 to-transparent rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="space-y-20">
          <div className="text-center mb-16 reveal">
            <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full glass-light border border-indigo-200 mb-8 shadow-lg shadow-indigo-100/50">
              <Dna className="h-5 w-5 text-indigo-600" />
              <span className="text-sm font-bold text-indigo-700 tracking-wide">ABOUT OUR LAB</span>
            </div>
            <h2 className="text-5xl md:text-7xl font-bold text-slate-900 mb-8 leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.02em' }}>
              Pioneering <span className="gradient-text-vibrant">Systems Biology</span>
            </h2>
            <div className="w-40 h-2 bg-gradient-to-r from-indigo-600 via-cyan-500 to-amber-500 mx-auto rounded-full shadow-lg"></div>
          </div>

          <div className="reveal">
            <div className="relative glass-light p-12 md:p-16 rounded-[32px] shadow-2xl border-2 border-indigo-100 hover-glow overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-indigo-500/10 to-transparent rounded-full"></div>
              <div className="absolute bottom-0 left-0 w-40 h-40 bg-gradient-to-tr from-cyan-500/10 to-transparent rounded-full"></div>
              <p className="text-xl md:text-2xl text-slate-700 leading-relaxed relative z-10 font-light">
                {aboutContent.description}
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-10">
            <div className="reveal group">
              <div className="relative h-full glass-light p-12 rounded-[32px] shadow-xl hover-lift border-2 border-indigo-100/50 overflow-hidden">
                <div className="absolute top-0 right-0 w-48 h-48 premium-gradient-soft rounded-full -mr-24 -mt-24"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-cyan-500/5 to-transparent rounded-full"></div>
                <div className="relative z-10">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl premium-gradient mb-8 group-hover:scale-110 transition-transform shadow-lg shadow-indigo-500/30">
                    <Cpu className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    Our Approach
                  </h3>
                  <p className="text-slate-600 leading-relaxed text-lg font-light">
                    {aboutContent.approach}
                  </p>
                </div>
                {/* Decorative line */}
                <div className="absolute bottom-0 left-0 right-0 h-1 premium-gradient opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </div>
            </div>

            <div className="reveal group">
              <div className="relative h-full glass-light p-12 rounded-[32px] shadow-xl hover-lift border-2 border-cyan-100/50 overflow-hidden">
                <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-bl from-cyan-500/10 to-transparent rounded-full -mr-24 -mt-24"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-amber-500/5 to-transparent rounded-full"></div>
                <div className="relative z-10">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl gold-gradient mb-8 group-hover:scale-110 transition-transform shadow-lg shadow-amber-500/30">
                    <TrendingUp className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    Translational Impact
                  </h3>
                  <p className="text-slate-600 leading-relaxed text-lg font-light">
                    {aboutContent.translational}
                  </p>
                </div>
                {/* Decorative line */}
                <div className="absolute bottom-0 left-0 right-0 h-1 gold-gradient opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
